import { PlayLayout } from '@app/play-now/routes/PlayLayout';
import { setupPlayUtils } from '@app/play-now/routes/remix-route-helper';
import { ClientMetaFunction, mergeMeta } from '@app/utils/remix/play-now-meta';
import { loadTvProgramGuide } from '@play-now/video/api/load-tv-program-guide';
import { loadTvProgramGuideNonSrg } from '@play-now/video/api/load-tv-program-guide-non-srg';
import { getRemixMeta } from '@play-now/video/components/Meta/PlayMetaData';
import { PlayConfig } from '@play-now/video/config/PlayConfig';
import { queryKeys } from '@play-now/video/config/use-query';
import { useInitialData } from '@play-now/video/config/VideoAppContext';
import { getTvGuideDateVariant, getValidDate, getTvGuideMeta } from '@play-now/video/pages/TvGuidePage/tv-guide-helpers';
import { TvGuidePage } from '@play-now/video/pages/TvGuidePage/TvGuidePage';
import { slugToday, slugTomorrow, tvGuidePath } from '@play-now/video/pages/TvGuidePage/TvGuidePage.route';
import { RedirectStatus } from '@play-now/video/routes/Routes';
import { isoDate as toIsoDate } from '@play-now/video/utils/date-formatter';
import { data, LoaderFunctionArgs, redirect } from '@remix-run/node';
import { Params, useLoaderData } from '@remix-run/react';
import { i18n as I18n } from 'i18next';

const checkRedirect = (
  i18n: I18n,
  baseUrl: string,
  isoDateParam?: string
): { needsRedirect: false } | { needsRedirect: true; path: string; status: RedirectStatus } => {
  const { t } = i18n;
  const dateVariant = getTvGuideDateVariant(getValidDate(isoDateParam));
  const needsRedirect = !!isoDateParam && dateVariant !== 'date' && ![slugToday(t), slugTomorrow(t)].includes(isoDateParam);

  if (!needsRedirect) return { needsRedirect };

  switch (dateVariant) {
    case 'tomorrow':
      return {
        needsRedirect,
        path: `${baseUrl}${tvGuidePath(i18n, slugTomorrow(i18n.t))}`,
        status: 302,
      };
    default:
      return {
        needsRedirect,
        path: `${baseUrl}${tvGuidePath(i18n, slugToday(i18n.t))}`,
        status: 302,
      };
  }
};

type RedirectParams = {
  params: Params;
  config: PlayConfig;
  i18n: I18n;
};
const getRedirectRoute = async ({ params, config, i18n }: RedirectParams) => {
  if (!params.isoDate) {
    return redirect(`${config.baseUrl}${tvGuidePath(i18n, slugToday(i18n.t))}`, 301);
  }
  const check = checkRedirect(i18n, config.baseUrl, params.isoDate);
  if (check.needsRedirect) {
    return redirect(check.path, check.status);
  }
};

export const loader = async (args: LoaderFunctionArgs) => {
  const { config, axios, params, i18n } = setupPlayUtils(args);
  const redirectResponse = await getRedirectRoute({ params, config, i18n });
  if (redirectResponse) return redirectResponse;

  const dateString = toIsoDate(getValidDate(params.isoDate));
  const provider = config.tvGuide?.tvGuideProviders[0];

  if (provider) {
    const program = await (provider === 'non-srg'
      ? loadTvProgramGuideNonSrg(dateString, axios, true)
      : loadTvProgramGuide(dateString, axios, [provider], true));

    const { title, description } = getTvGuideMeta(i18n.t, new Date(dateString));
    const metaData = getRemixMeta({ config, title, description });

    return data({ tvGuide: { [queryKeys.TvProgramGuide(dateString, provider)]: program }, meta: metaData });
  }
};

export const meta: ClientMetaFunction<typeof loader> = ({ data: res, matches }) => mergeMeta({ data: { meta: res?.meta }, matches });

const TvGuidePageRoute = () => {
  const loaderData = useLoaderData<typeof loader>();
  const tvGuide = loaderData?.tvGuide;
  const initialData = useInitialData();
  return (
    <PlayLayout pageId='tvGuide'>
      <TvGuidePage initialData={{ ...initialData, tvGuide }} />
    </PlayLayout>
  );
};

export default TvGuidePageRoute;
